import React, { useEffect, useState } from 'react';
import useScrollOverflow from '@hooks/useScrollOverflow/useScrollOverflow';
import clsx from 'clsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import ActionMenu, { ActionMenuItem } from '@components/RingilComponents/ActionMenu.tsx';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog.tsx';
import SvgDots from '@icons/3-dots-horizontal-inverted.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import SvgAngleLeft from '@icons/angle-left.svg?react';
import SvgAngleRight from '@icons/angle-right.svg?react';
import SvgAngleDown from '@icons/angle-down.svg?react';
import ScrollEdgeArrow from '@components/RingilComponents/ScrollEdgeArrow';
import SvgPencil from '@icons/pencil.svg?react';
import SvgShare from '@icons/share-nodes-inverted.svg?react';
import SvgTrash from '@icons/delete-trash.svg?react';
import SvgCopy from '@icons/CoCopy.tsx';
import type { TableView } from './types';
import type { TableViewsState } from './useTableViews';
import { canMoveView, canRemoveView, removeActionFor } from './viewOps';
import ViewNameSidebar from './ViewNameSidebar';
import ShareViewSidebar from './ShareViewSidebar';
import HiddenViewsSidebar from './HiddenViewsSidebar';
import SvgEyeSlash from '@icons/eye-slash.svg?react';

interface ComponentProps {
  state: TableViewsState;
  userId: string;
  /** Items the table adds at the top of the active tab's menu (default filters, table settings). */
  extraMenuItems?: ActionMenuItem[];
}

type NameDialog = { kind: 'create' } | { kind: 'rename'; view: TableView } | { kind: 'saveAsNew' } | null;

/** Tab look from the Figma (21940:60889): grey tabs, the open one white with brand text, on a brand line. */
const tabBase = 'inline-flex shrink-0 items-center gap-1.5 h-8 px-2.5 border-0 font-[inherit] text-xs font-medium tracking-[0.6px] whitespace-nowrap';
const SCROLL_STEP = 200;

/**
 * The tab strip of a RingilDataTable v3: one tab per saved view, the active one with its "…" menu,
 * "+" for a new tab, and — while the open tab has unsaved changes — a dot on it and a Save button
 * with a menu of the ways out of that state. Scrolls sideways with arrows once the tabs outgrow the table.
 */
const TabStrip: React.FC<ComponentProps> = ({ state, userId, extraMenuItems = [] }) => {
  const { tr } = useTranslationLgs();
  const { views, activeView, modified, actions } = state;
  // Re-measured on every render, so new or removed tabs show or hide the arrows too.
  const { scrollRef: scrollerRef, overflowLeft, overflowRight } = useScrollOverflow();
  const [nameDialog, setNameDialog] = useState<NameDialog>(null);
  const [confirmRemove, setConfirmRemove] = useState<TableView | null>(null);
  // "Sdílet pohled" asks in a sidebar (Figma 21944:65259); taking a shared tab back asks in a dialog.
  const [sharing, setSharing] = useState<TableView | null>(null);
  const [confirmUnshare, setConfirmUnshare] = useState<TableView | null>(null);
  const [hiddenOpen, setHiddenOpen] = useState(false);

  // Keep the open tab in sight when it changes, e.g. after "+" or a duplicate added it at the far end.
  useEffect(() => {
    scrollerRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeView?.id]);

  const scrollBy = (delta: number) => scrollerRef.current?.scrollBy({ left: delta, behavior: 'smooth' });

  const menuFor = (view: TableView): ActionMenuItem[] => {
    const own = state.isOwn(view);
    const removable = canRemoveView(views);
    const hide = removeActionFor(view, userId) === 'hide';
    return [
      ...extraMenuItems,
      {
        key: 'move-left',
        label: tr('TableViews.moveLeft', 'Move left'),
        icon: <SvgAngleLeft />,
        disabled: !canMoveView(views, view.id, -1),
        onClick: () => actions.move(view.id, -1),
        separatorBefore: extraMenuItems.length > 0,
      },
      {
        key: 'move-right',
        label: tr('TableViews.moveRight', 'Move right'),
        icon: <SvgAngleRight />,
        disabled: !canMoveView(views, view.id, 1),
        onClick: () => actions.move(view.id, 1),
      },
      {
        key: 'rename',
        label: tr('generic.rename', 'Rename'),
        icon: <SvgPencil />,
        disabled: !own,
        disabledReason: tr('TableViews.onlyOwnerCanEdit', 'Only the author of the view can change it.'),
        onClick: () => setNameDialog({ kind: 'rename', view }),
      },
      { key: 'duplicate', label: tr('generic.duplicate', 'Duplicate'), icon: <SvgCopy />, onClick: () => actions.duplicate(view.id) },
      view.visibility === 'COMPANY' && own
        ? { key: 'unshare', label: tr('TableViews.stopSharing', 'Stop sharing'), icon: <SvgShare />, onClick: () => setConfirmUnshare(view) }
        : {
            key: 'share',
            label: tr('TableViews.share', 'Share with colleagues'),
            icon: <SvgShare />,
            disabled: !own,
            disabledReason: tr('TableViews.onlyOwnerCanEdit', 'Only the author of the view can change it.'),
            onClick: () => setSharing(view),
          },
      // Only while there is something to bring back, so the menu doesn't offer an empty list.
      ...(state.hiddenViews.length > 0
        ? [
            {
              key: 'hidden-views',
              label: tr('TableViews.hiddenViewsCount', 'Hidden views ({{count}})', { count: String(state.hiddenViews.length) }),
              icon: <SvgEyeSlash />,
              onClick: () => setHiddenOpen(true),
            },
          ]
        : []),
      {
        key: 'remove',
        label: hide ? tr('generic.hide', 'Hide') : tr('generic.delete', 'Delete'),
        icon: <SvgTrash />,
        danger: true,
        separatorBefore: true,
        disabled: !removable,
        disabledReason: tr('TableViews.lastViewCannotBeRemoved', 'The last view cannot be removed.'),
        onClick: () => setConfirmRemove(view),
      },
    ];
  };

  // A colleague's shared tab says whose it is at the foot of its menu (Figma 23555:79515), not on the tab itself.
  const sharedByFor = (view: TableView): string | undefined => {
    if (view.visibility !== 'COMPANY' || state.isOwn(view)) return undefined;
    return view.ownerFullName
      ? tr('TableViews.sharedBy', 'Shared by {{name}}', { name: view.ownerFullName })
      : tr('TableViews.sharedWithColleagues', 'Shared with colleagues');
  };

  const removingHides = confirmRemove ? removeActionFor(confirmRemove, userId) === 'hide' : false;

  // The ways out of unsaved changes: one button for the usual one, the rest (and it again) in the menu beside it.
  const ownActive = activeView ? state.isOwn(activeView) : false;
  const saveAsNew = () => setNameDialog({ kind: 'saveAsNew' });
  const unsavedMenu: ActionMenuItem[] = [
    {
      key: 'save',
      label: tr('generic.save', 'Save'),
      brand: true,
      disabled: !ownActive,
      disabledReason: tr('TableViews.onlyOwnerCanEdit', 'Only the author of the view can change it.'),
      onClick: () => actions.save(),
      dataTestId: 'table-view-menu-save',
    },
    { key: 'save-as-new', label: tr('TableViews.saveAsNew', 'Save as new'), brand: true, onClick: saveAsNew, dataTestId: 'table-view-menu-save-as-new' },
    {
      key: 'discard',
      brand: true,
      label: tr('generic.discardChanges', 'Discard changes'),
      separatorBefore: true,
      onClick: () => actions.revert(),
      dataTestId: 'table-view-discard',
    },
  ];

  return (
    <div className="flex items-end gap-2 border-0 border-b-2 border-solid border-content-brand font-['DM_Sans']" data-testid="table-view-tabs">
      {/* The arrows (after the strip, so they're drawn over it) lie on its edges, not beside it: the tabs don't move when one appears or goes. */}
      <div className="relative flex min-w-0">
        <div ref={scrollerRef} className="relative z-0 flex min-w-0 items-end gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist">
          {views.map(view => {
            const active = view.id === activeView?.id;
            return (
              <div
                key={view.id}
                data-active={active}
                className={clsx(tabBase, active ? 'bg-base-secondary text-content-brand' : 'bg-content-quaternary text-content-primary')}
                data-testid={`table-view-tab-${view.id}`}
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => actions.select(view.id)}
                  className={clsx(
                    'p-0 border-0 bg-transparent cursor-pointer font-[inherit] text-[length:inherit] tracking-[inherit] [color:inherit]',
                    !active && 'hover:text-content-brand',
                  )}
                >
                  {view.name}
                </button>
                {active && modified && (
                  <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-content-brand" aria-label={tr('generic.unsavedChanges', 'Unsaved changes')} role="img" />
                )}
                {active && (
                  <ActionMenu
                    items={menuFor(view)}
                    icon={<SvgDots className="w-3 h-3" />}
                    mode="text-only"
                    density="compact"
                    tooltip={tr('generic.moreActions', 'More actions')}
                    triggerClassName="h-auto w-auto p-0 bg-transparent"
                    dataTestId="table-view-menu"
                    footer={sharedByFor(view)}
                  />
                )}
              </div>
            );
          })}
        </div>
        {overflowLeft && <ScrollEdgeArrow side="left" tooltip={tr('generic.previous', 'Previous')} onClick={() => scrollBy(-SCROLL_STEP)} />}

        {overflowRight && <ScrollEdgeArrow side="right" tooltip={tr('generic.next', 'Next')} onClick={() => scrollBy(SCROLL_STEP)} />}
      </div>
      {/* Outside the scrolling strip, so a new tab is always one click away. */}
      <RingilButton
        mode="text-only"
        icon={<SvgPlus className="w-3 h-3" />}
        tooltip={tr('TableViews.newView', 'New view')}
        onClick={() => setNameDialog({ kind: 'create' })}
        className={clsx(tabBase, 'bg-content-quaternary text-content-brand rounded-none')}
        dataTestId="table-view-add"
      />

      {modified && activeView && (
        <div className="ml-auto flex shrink-0 items-center gap-2 pb-1.5 text-xs" data-testid="table-view-unsaved-actions">
          {/* A colleague's view can't be overwritten, so its button saves a copy instead. */}
          <RingilButton
            mode="text-only"
            label={ownActive ? tr('generic.save', 'Save') : tr('TableViews.saveAsNew', 'Save as new')}
            onClick={ownActive ? () => actions.save() : saveAsNew}
            className="h-auto px-0 bg-transparent text-xs"
            dataTestId={ownActive ? 'table-view-save' : 'table-view-save-as-new'}
          />
          <ActionMenu
            items={unsavedMenu}
            icon={<SvgAngleDown className="w-3 h-3 [&_path]:fill-current" />}
            mode="text-only"
            density="compact"
            rotateIconWhenOpen
            tooltip={tr('TableViews.saveOptions', 'Save options')}
            triggerClassName="h-auto w-auto p-0 bg-transparent"
            dataTestId="table-view-unsaved-menu"
          />
        </div>
      )}

      <ViewNameSidebar
        visible={nameDialog !== null}
        heading={
          nameDialog?.kind === 'rename'
            ? tr('generic.rename', 'Rename')
            : nameDialog?.kind === 'saveAsNew'
            ? tr('TableViews.saveAsNew', 'Save as new')
            : tr('TableViews.newView', 'New view')
        }
        continueLabel={nameDialog?.kind === 'rename' ? tr('generic.save', 'Save') : tr('generic.create', 'Create')}
        initialName={nameDialog?.kind === 'rename' ? nameDialog.view.name : ''}
        onClose={() => setNameDialog(null)}
        onSubmit={name => {
          if (nameDialog?.kind === 'rename') actions.rename(nameDialog.view.id, name);
          else if (nameDialog?.kind === 'saveAsNew') actions.saveAsNew(name);
          else actions.create(name);
        }}
      />

      <HiddenViewsSidebar visible={hiddenOpen} views={state.hiddenViews} onUnhide={viewId => actions.unhide(viewId)} onClose={() => setHiddenOpen(false)} />

      <ShareViewSidebar
        visible={sharing !== null}
        modified={modified && sharing?.id === activeView?.id}
        onClose={() => setSharing(null)}
        onConfirm={() => sharing && actions.share(sharing.id, 'COMPANY')}
      />

      <ConfirmDialog
        visible={confirmUnshare !== null}
        dialogTitle={tr('TableViews.stopSharing', 'Stop sharing')}
        dialogMessage={tr('TableViews.stopSharingMessage', 'Your colleagues will no longer see the view “{{name}}”. It stays yours.', { name: confirmUnshare?.name })}
        onAcceptButtonText={tr('TableViews.stopSharing', 'Stop sharing')}
        onCancelButtonText={tr('generic.cancel', 'Cancel')}
        onCancel={() => setConfirmUnshare(null)}
        onConfirm={() => {
          if (confirmUnshare) actions.share(confirmUnshare.id, 'PRIVATE');
          setConfirmUnshare(null);
        }}
      />

      <ConfirmDialog
        visible={confirmRemove !== null}
        dialogTitle={removingHides ? tr('generic.hide', 'Hide') : tr('generic.delete', 'Delete')}
        dialogMessage={
          removingHides
            ? tr('TableViews.hideMessage', 'The view “{{name}}” will no longer show for you. Your colleagues keep it.', { name: confirmRemove?.name })
            : confirmRemove?.visibility === 'COMPANY'
            ? tr('TableViews.deleteSharedMessage', 'Delete the view “{{name}}”? It is shared, so your colleagues lose it too. This cannot be undone.', {
                name: confirmRemove?.name,
              })
            : tr('TableViews.deleteMessage', 'Delete the view “{{name}}”? This cannot be undone.', { name: confirmRemove?.name })
        }
        onAcceptButtonText={removingHides ? tr('generic.hide', 'Hide') : tr('generic.delete', 'Delete')}
        onCancelButtonText={tr('generic.cancel', 'Cancel')}
        onCancel={() => setConfirmRemove(null)}
        onConfirm={() => {
          if (confirmRemove) actions.remove(confirmRemove.id);
          setConfirmRemove(null);
        }}
      />
    </div>
  );
};

export default TabStrip;
